import { clockMinutes, minutesToHHMM } from './clock'

// The one ordered statement of what makes a practice-preferences time answer
// self-contradicting. Shared because BOTH layers have to make this call and
// they must make it identically: the coach form blocks the save so the refusal
// stays attached to the fields that caused it, and `validatePrefsInput` (the
// coach worker) refuses regardless, because it is also `putPrefs`'s own gate
// with no route in front of it.
//
// ⚠️ The ORDER is the load-bearing part, and it is why this is a shared module
// rather than two checks that happen to agree. With both the range and the
// preferred start wrong, whichever fault a layer reports first is the one the
// coach is told to fix; if the two layers disagreed, fixing the message on
// screen would clear the client's check and then be refused by the server with
// copy contradicting the message that had just disappeared. A comment in each
// file cannot fail when they drift. This can.
//
// The COPY deliberately does not live here. The form quotes both times back on
// a 12-hour clock (an AM/PM slip is how this happens on a phone); the server
// has no clock formatter and does not need one. Each layer renders the fault
// its own way and neither imports the other's words.

export type PracticeTimeRangeFault =
  | 'inverted'
  | 'preferred_before_earliest'
  | 'preferred_after_latest'
  | 'earliest_before_floor'
  | 'latest_before_floor'
  | 'preferred_before_floor'

/** No practice in this region starts before 5pm — learned from the scheduler
 *  working the real Fall 2026 data, 2026-08-28. Fields are in use by schools
 *  and daylight is the constraint at the other end, so an earlier answer is not
 *  a preference the solver can honor, it is an answer that cannot be met. */
export const PRACTICE_EARLIEST_START = '17:00'

/** The shortest practice any division runs. */
export const MIN_PRACTICE_MINUTES = 60

/** ...so nothing can END before 6pm, and this is DERIVED rather than typed a
 *  second time. The 18:00 floor is not an independent fact: it is the 17:00
 *  start plus the shortest practice. Writing both as literals is how they come
 *  apart the first time the minimum changes. */
export const PRACTICE_EARLIEST_END = minutesToHHMM(clockMinutes(PRACTICE_EARLIEST_START) + MIN_PRACTICE_MINUTES)

/** Blank is carried as `''` by the form's draft and as `null` by the worker's
 *  validated payload, so both are accepted and both mean "not answered". */
export interface PracticeTimeRangeInput {
  timeEarliest: string | null
  timeLatest: string | null
  timePreferred: string | null
}

/** The first fault in a time answer, or null if there is none.
 *
 *  Every comparison requires BOTH sides: an unanswered field cannot contradict
 *  anything, which is what keeps a half-filled range (mid-edit on the form)
 *  from being reported as an error. Times are compared as strings because
 *  zero-padded `HH:MM` sorts chronologically, which is also the shape both
 *  callers have already validated. */
export function practiceTimeRangeFault(t: PracticeTimeRangeInput): PracticeTimeRangeFault | null {
  const earliest = t.timeEarliest || null
  const latest = t.timeLatest || null
  const preferred = t.timePreferred || null
  if (earliest && latest && earliest > latest) return 'inverted'
  // The floors come BEFORE the preferred-time checks and AFTER `inverted`, and
  // the position is deliberate. An inverted range is the more fundamental
  // complaint, so it still wins; but a coach whose whole window sits before 5pm
  // must be told THAT rather than something about their preferred time, which
  // they would fix without fixing the actual problem.
  //
  // No separate rule for `preferred`: it is already refused when it falls
  // outside the hard bounds, so a 5pm floor on `earliest` reaches it.
  if (earliest && earliest < PRACTICE_EARLIEST_START) return 'earliest_before_floor'
  if (latest && latest < PRACTICE_EARLIEST_END) return 'latest_before_floor'
  // ⚠️ AND the preferred time on its own. An earlier version of this said no
  // separate rule was needed because a preferred time outside the hard bounds
  // is already refused — true only when bounds EXIST. None of the three inputs
  // is required on the coach form, so `timePreferred: '16:00'` with both bounds
  // blank passed every check above and stored. Reported as the start-floor
  // fault because that is what it is: an answer asking to practice before 5pm.
  // Its OWN fault, not the start-floor one. Reusing `earliest_before_floor`
  // here produced copy telling a coach to raise an earliest start that was
  // already legal — naming the wrong field is how a refusal sends someone to
  // edit something that is not the problem.
  //
  // Ordered before `preferred_before_earliest`, which it can co-occur with: the
  // floor is the harder constraint, and a coach told only that their preferred
  // start is before their earliest would try to lower the earliest and be
  // refused again.
  if (preferred && preferred < PRACTICE_EARLIEST_START) return 'preferred_before_floor'
  // A target outside the hard bounds is a contradiction, not a preference.
  if (preferred && earliest && preferred < earliest) return 'preferred_before_earliest'
  if (preferred && latest && preferred > latest) return 'preferred_after_latest'
  return null
}
